import React, { useEffect, useState } from 'react';

// Types
import { Filters } from '@src/types/search';
import SearchFilter from './SearchFilter';

interface SearchFiltersProps {
  filters?: Filters[];
  className?: string;
  enabledFilters?: string[];
  settings: any;
}

export const SearchFilters: React.FC<SearchFiltersProps> = ({ className = '', enabledFilters = [], settings }) => {
  const [filters, setFilters] = useState<Filters | null>(null);
  const [appliedFilters, setAppliedFilters] = useState<Filters>([]);

  // Generate spacing classes from settings
  const spacingClasses = [
    settings.margin_top ? `mt-[${settings.margin_top}px]` : '',
    settings.margin_bottom ? `mb-[${settings.margin_bottom}px]` : '',
    settings.margin_left ? `ml-[${settings.margin_left}px]` : '',
    settings.margin_right ? `mr-[${settings.margin_right}px]` : '',
    settings.padding_top ? `pt-[${settings.padding_top}px]` : '',
    settings.padding_bottom ? `pb-[${settings.padding_bottom}px]` : '',
    settings.padding_left ? `pl-[${settings.padding_left}px]` : '',
    settings.padding_right ? `pr-[${settings.padding_right}px]` : ''
  ]
    .filter(Boolean)
    .join(' ');

  const handleFiltersUpdate = (event: Event) => {
    const { filters }: { filters: Filters } = (event as CustomEvent).detail || [];

    const availableFilters = filters.filter((filter) => enabledFilters.some((enabled) => enabled === filter.label));

    setFilters(availableFilters);
  };

  const handleAppliedFiltersUpdate = (event: Event) => {
    const { filters }: { filters: Filters } = (event as CustomEvent).detail || {};

    setAppliedFilters(filters);
  };

  const handleSearchStateChange = (event: Event) => {
    const { hasResults, query }: { hasResults: boolean; query: string } = (event as CustomEvent).detail || {};

    if (!hasResults && !query) {
      setFilters(null);
      setAppliedFilters([]);
    }
  };

  useEffect(() => {
    if (!enabledFilters || enabledFilters.length === 0) return;

    // Listen for filters data from search results
    window.addEventListener('searchrequest-filters-complete', handleFiltersUpdate);
    window.addEventListener('searchrequest-current-filters-updated', handleAppliedFiltersUpdate);
    window.addEventListener('search-state-changed', handleSearchStateChange);

    return () => {
      window.removeEventListener('searchrequest-filters-complete', handleFiltersUpdate);
      window.removeEventListener('searchrequest-current-filters-updated', handleAppliedFiltersUpdate);
      window.removeEventListener('search-state-changed', handleSearchStateChange);
    };
  }, [enabledFilters]);

  if (!enabledFilters || enabledFilters.length === 0 || !filters || filters.length === 0) return null;

  return (
    <div className={`w-full ${spacingClasses} ${className} space-y-4`}>
      {filters.map((filter, index) => (
        <SearchFilter
          key={index}
          filter={filter}
          appliedFilters={appliedFilters}
        />
      ))}
    </div>
  );
};
